import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Alert,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Popover,
  InputAdornment,
  Avatar,
  MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';
import { ProductPicker, UserPicker, RowThumb } from '../ui';
import HelpMark from '../help/HelpMark';
import OrderBasedInvoiceLineItemsTable from './OrderBasedInvoiceLineItemsTable';
import InvoiceNoteTemplates from './InvoiceNoteTemplates';

const emptyLineItem = () => ({ description: '', quantity: 1, unitPrice: 0, discount: 0 });

// Payment terms presets for the due date; '' means a custom (or no) date.
const DUE_TERMS = [15, 30, 60];
const localDatePlusDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const emptyInventoryLineItem = () => ({ productId: '', quantity: 1, discount: 0 });
const isProductBacked = (lineItem) => Object.prototype.hasOwnProperty.call(lineItem, 'productId');
const roundMoney = (value) => Math.round(value * 100) / 100;
const inventoryItemSignature = (item) => JSON.stringify([
  item.productName,
  item.description,
  item.unitPrice,
  item.unit,
  item.availableQuantity
]);

const normalizeInventoryItems = (items) => {
  const itemsByProductId = new Map();
  let hasInvalidProductId = false;

  items.forEach((item) => {
    if (typeof item.productId !== 'string' || !item.productId) {
      hasInvalidProductId = true;
      return;
    }
    const matches = itemsByProductId.get(item.productId) || [];
    matches.push(item);
    itemsByProductId.set(item.productId, matches);
  });

  const normalizedItems = [];
  const conflictingProductIds = [];
  itemsByProductId.forEach((matches, productId) => {
    const signature = inventoryItemSignature(matches[0]);
    if (matches.some(item => inventoryItemSignature(item) !== signature)) {
      conflictingProductIds.push(productId);
      return;
    }
    normalizedItems.push(matches[0]);
  });

  return { normalizedItems, conflictingProductIds, hasInvalidProductId };
};

// Bill-to values seeded from the picked user; the admin can then edit them
// as a one-off for this invoice only (never written back to the user).
// Default order-based period: the last month up to today. Dates are taken in
// UTC because the server compares them against UTC order timestamps — a local
// "today" would drop an order placed this evening.
const toDateInput = (date) => date.toISOString().slice(0, 10);
const defaultPeriod = () => {
  const end = new Date();
  const start = new Date(end);
  start.setUTCMonth(start.getUTCMonth() - 1);
  return { start: toDateInput(start), end: toDateInput(end) };
};

const initialBillTo = (user) => {
  const billing = user?.shippingAddress || {};
  return {
    name: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username || '',
    companyName: billing.companyName || '',
    addressLine1: billing.addressLine1 || '',
    addressLine2: billing.addressLine2 || '',
    city: billing.city || '',
    state: billing.state || '',
    postalCode: billing.postalCode || '',
    country: billing.country || ''
  };
};

// "Bill to" card shown once a company user is picked as the recipient. Two
// hover actions: the pencil edits the block inline (one-off, this invoice
// only), the open-in-new icon jumps to that user's Edit User form. A saved
// invoice contact reuses the card read-only, so both handlers are optional.
const BillToBox = ({ t, value, editing, onStartEdit, onStopEdit, onChange, onOpenUser }) => {
  if (!value) return null;
  const hasActions = !!(onStartEdit || onOpenUser);
  const locality = [value.city, value.state, value.postalCode].filter(Boolean).join(' ');
  const lines = [
    value.companyName,
    value.addressLine1,
    value.addressLine2,
    locality,
    value.country
  ].filter(Boolean);
  const field = (key, label, xs = 6) => (
    <Grid item xs={xs}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value[key]}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
      />
    </Grid>
  );
  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${radius.md}px`,
        p: 1.5,
        pr: 9,
        maxWidth: editing ? 480 : 360,
        '&:hover .billto-actions, &:focus-within .billto-actions': { opacity: 1 }
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
        {t('Bill to', { defaultValue: 'Bill to' })}
      </Typography>
      {editing ? (
        <Grid container spacing={1} sx={{ mt: 0 }}>
          {field('name', t('Full name', { defaultValue: 'Full name' }), 12)}
          {field('companyName', t('Company Name'), 12)}
          {field('addressLine1', t('Address'), 12)}
          {field('city', t('City'))}
          {field('state', t('State / Province'))}
          {field('postalCode', t('Postal Code'))}
          {field('country', t('Country'))}
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {t('Changes apply to this invoice only', { defaultValue: 'Changes apply to this invoice only' })}
            </Typography>
            <Button size="small" onClick={onStopEdit}>{t('Done', { defaultValue: 'Done' })}</Button>
          </Grid>
        </Grid>
      ) : (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{value.name}</Typography>
          {lines.map((line) => (
            <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
          ))}
          {lines.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('No billing information on file', { defaultValue: 'No billing information on file' })}
            </Typography>
          )}
        </>
      )}
      {hasActions && (
        <Box className="billto-actions" sx={{ position: 'absolute', top: 6, right: 6, opacity: editing ? 1 : 0, transition: 'opacity 120ms' }}>
          {!editing && (
            <IconButton
              size="small"
              aria-label={t('Edit for this invoice', { defaultValue: 'Edit for this invoice' })}
              onClick={onStartEdit}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            aria-label={t('Edit user billing info', { defaultValue: 'Edit user billing info' })}
            onClick={onOpenUser}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const InvoiceCreateDialog = ({
  open,
  onClose,
  onSaved,
  invoiceClient,
  users,
  invoiceContacts = [],
  noteTemplates = [],
  onNoteTemplatesChanged,
  editInvoice: editInvoiceProp,
  reviseInvoice = null,
  initialError = ''
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Revising an issued custom invoice reuses the whole edit form (recipient,
  // line items, tax/discount/notes); only the submit endpoint differs.
  const editInvoice = editInvoiceProp || reviseInvoice;
  const isRevise = !editInvoiceProp && !!reviseInvoice;
  const isEdit = !!editInvoice;

  const [invoiceType, setInvoiceType] = useState('order_based');
  const [includeItemBreakdown, setIncludeItemBreakdown] = useState(true);
  const [recipientUserId, setRecipientUserId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [taxRate, setTaxRate] = useState('0');
  const [discountTotal, setDiscountTotal] = useState('0');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTerms, setDueTerms] = useState('');

  // Custom-invoice recipient: 'user' (existing company user), 'contact' (existing invoice contact),
  // or 'new' (admin typed a name not matching any existing contact). Only used when !isEdit.
  const [recipientSource, setRecipientSource] = useState('');
  const [contactId, setContactId] = useState('');

  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [lineItems, setLineItems] = useState([emptyLineItem()]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [conflictingInventoryProductIds, setConflictingInventoryProductIds] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState('');

  const [preview, setPreview] = useState(null);
  // Orders ticked in the preview; every previewed order starts selected.
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // One-off bill-to override: seeded from the picked user, edited inline,
  // sent with the create request only when actually touched.
  const [billTo, setBillTo] = useState(null);
  const [billToEditing, setBillToEditing] = useState(false);
  const [billToEdited, setBillToEdited] = useState(false);

  // Product picker popover: which line item row it's open for, and its search text.
  const [productPickerAnchorEl, setProductPickerAnchorEl] = useState(null);
  const [productPickerIndex, setProductPickerIndex] = useState(null);
  const [productPickerSearch, setProductPickerSearch] = useState('');

  // User picker popover: shared by the order-based "Recipient" and custom
  // "Recipients" fields, since only one is ever rendered at a time.
  const [userPickerAnchorEl, setUserPickerAnchorEl] = useState(null);
  const [userPickerMode, setUserPickerMode] = useState(null);
  const [userPickerSearch, setUserPickerSearch] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;

    setProductPickerAnchorEl(null);
    setProductPickerIndex(null);
    setProductPickerSearch('');
    setUserPickerAnchorEl(null);
    setUserPickerMode(null);
    setUserPickerSearch('');

    setError(initialError);
    setPreview(null);
    setSelectedOrderIds([]);
    setBillTo(null);
    setBillToEditing(false);
    setBillToEdited(false);
    setInventoryItems([]);
    setConflictingInventoryProductIds([]);
    setInventoryError('');

    if (editInvoice) {
      setInvoiceType(editInvoice.invoiceType);
      setIncludeItemBreakdown(editInvoice.includeItemBreakdown !== false);
      setTaxRate(String(editInvoice.taxRate ?? 0));
      setDiscountTotal(String(editInvoice.discountTotal ?? 0));
      setNotes(editInvoice.notes || '');
      setDueDate(editInvoice.dueDate ? editInvoice.dueDate.slice(0, 10) : '');
      setDueTerms('');
      setRecipientUserId(editInvoice.recipient?.recipientUserId || '');
      setPeriodStart(editInvoice.periodStart ? editInvoice.periodStart.slice(0, 10) : '');
      setPeriodEnd(editInvoice.periodEnd ? editInvoice.periodEnd.slice(0, 10) : '');
      setRecipientName(editInvoice.recipient?.name || '');
      setRecipientEmail(editInvoice.recipient?.email || '');
      setRecipientPhone(editInvoice.recipient?.phone || '');
      setRecipientAddress(editInvoice.recipient?.address || '');
      setLineItems(
        editInvoice.lineItems && editInvoice.lineItems.length > 0
          ? editInvoice.lineItems.map(li => (
              isProductBacked(li)
                ? {
                    productId: li.productId,
                    productName: li.productName,
                    description: li.description,
                    unit: li.unit,
                    quantity: li.quantity,
                    unitPrice: li.unitPrice,
                    discount: li.discount,
                    // The API stores discount as a flat total for the line; the UI shows it
                    // per unit (see handleDiscountPerUnitChange), so derive that once on load.
                    discountPerUnit: Number(li.quantity) > 0
                      ? roundMoney(Number(li.discount || 0) / Number(li.quantity))
                      : Number(li.discount || 0)
                  }
                : {
                    description: li.description,
                    quantity: li.quantity,
                    unitPrice: li.unitPrice,
                    discount: li.discount
                  }
            ))
          : [emptyLineItem()]
      );
    } else {
      setInvoiceType('order_based');
      setIncludeItemBreakdown(true);
      setTaxRate('0');
      setDiscountTotal('0');
      setNotes('');
      setDueDate('');
      setDueTerms('');
      setRecipientUserId('');
      const period = defaultPeriod();
      setPeriodStart(period.start);
      setPeriodEnd(period.end);
      setRecipientSource('');
      setContactId('');
      setRecipientName('');
      setRecipientEmail('');
      setRecipientPhone('');
      setRecipientAddress('');
      setLineItems([emptyLineItem()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editInvoice, initialError]);

  const customFieldsVisible = isEdit ? editInvoice?.invoiceType === 'custom' : invoiceType === 'custom';
  const hasInventoryLineItems = lineItems.some(isProductBacked);
  const inventoryItemsByProductId = useMemo(() => (
    new Map(inventoryItems.map(item => [item.productId, item]))
  ), [inventoryItems]);
  const conflictingInventoryProductIdSet = useMemo(() => (
    new Set(conflictingInventoryProductIds)
  ), [conflictingInventoryProductIds]);

  const fetchInventoryItems = async () => {
    if (!invoiceClient) return;
    setInventoryLoading(true);
    setInventoryError('');
    try {
      const companyId = localStorage.getItem('companyId');
      const response = await invoiceClient.getInventoryItems(companyId);
      const items = Array.isArray(response.data) ? response.data : [];
      const { normalizedItems, conflictingProductIds, hasInvalidProductId } = normalizeInventoryItems(items);
      // The issued invoice already deducted its own quantities from stock, so
      // while revising, those units are available to re-allocate here.
      let effectiveItems = normalizedItems;
      if (isRevise) {
        const originalQuantities = {};
        (reviseInvoice.lineItems || []).forEach((li) => {
          if (li.productId) {
            originalQuantities[li.productId] = (originalQuantities[li.productId] || 0) + (Number(li.quantity) || 0);
          }
        });
        effectiveItems = normalizedItems.map((item) => (
          originalQuantities[item.productId]
            ? { ...item, availableQuantity: Number(item.availableQuantity) + originalQuantities[item.productId] }
            : item
        ));
      }
      setInventoryItems(effectiveItems);
      setConflictingInventoryProductIds(conflictingProductIds);
      const hasInvalidInventoryData = hasInvalidProductId
        || conflictingProductIds.length > 0
        || normalizedItems.some(item => (
          !Number.isFinite(Number(item.unitPrice))
          || Number(item.unitPrice) < 0
          || !Number.isFinite(Number(item.availableQuantity))
          || Number(item.availableQuantity) < 0
        ));
      if (hasInvalidInventoryData) {
        console.error('Invalid inventory data returned for invoice line items', {
          conflictingProductIds,
          hasInvalidProductId
        });
        setInventoryError(t('Some inventory data is invalid. Please contact support.'));
      }
    } catch (err) {
      console.error('Error fetching invoice inventory items', err);
      setInventoryError(err.response?.data?.error || t('Unable to load inventory items.'));
    } finally {
      setInventoryLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !customFieldsVisible || !hasInventoryLineItems) return;
    fetchInventoryItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customFieldsVisible, hasInventoryLineItems, invoiceClient]);

  const handleClose = () => {
    setError('');
    onClose();
  };

  // Inserting a template replaces whatever is in Notes (product decision:
  // picking a template means "use this note", never "add to what I typed").
  const handleInsertNoteTemplate = (content) => {
    setNotes(content);
  };

  const openProductPicker = (index, anchorEl) => {
    setProductPickerIndex(index);
    setProductPickerAnchorEl(anchorEl);
    setProductPickerSearch('');
  };

  const closeProductPicker = () => {
    setProductPickerAnchorEl(null);
    setProductPickerIndex(null);
    setProductPickerSearch('');
  };

  const openUserPicker = (mode, anchorEl) => {
    setUserPickerMode(mode);
    setUserPickerAnchorEl(anchorEl);
    setUserPickerSearch('');
  };

  const closeUserPicker = () => {
    setUserPickerAnchorEl(null);
    setUserPickerMode(null);
    setUserPickerSearch('');
  };

  const handleUserPickerSelect = (userId) => {
    if (userPickerMode === 'custom') {
      setRecipientSource('user');
      setRecipientUserId(userId);
      setContactId('');
      setRecipientName('');
      setRecipientEmail('');
      setRecipientPhone('');
      setRecipientAddress('');
    } else {
      setRecipientUserId(userId);
    }
    setBillTo(initialBillTo(users.find((u) => u._id === userId)));
    setBillToEditing(false);
    setBillToEdited(false);
    closeUserPicker();
  };

  const handlePreview = async () => {
    if (!recipientUserId || !periodStart || !periodEnd) return;
    setError('');
    setPreviewLoading(true);
    setPreview(null);
    setSelectedOrderIds([]);
    try {
      const companyId = localStorage.getItem('companyId');
      const res = await invoiceClient.previewInvoice({
        companyId,
        recipientUserId,
        periodStart: `${periodStart}T00:00:00`,
        periodEnd: `${periodEnd}T23:59:59`
      });
      setPreview(res.data);
      setSelectedOrderIds((res.data?.lineItems || []).map((li) => li.sourceOrderId).filter(Boolean));
    } catch (err) {
      console.error('Error previewing invoice', err);
      setError(err.response?.data?.error || t('Something went wrong'));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Load the recipient's accepted orders as soon as they are picked (and
  // again when the period changes) so the admin never has to press Preview
  // to find out what will be invoiced. Debounced so typing a date does not
  // fire a request per keystroke.
  useEffect(() => {
    if (!open || isEdit || invoiceType !== 'order_based') return undefined;
    if (!recipientUserId || !periodStart || !periodEnd) return undefined;
    const timer = setTimeout(() => { handlePreview(); }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, invoiceType, recipientUserId, periodStart, periodEnd]);

  const handleAddLineItem = () => {
    setLineItems([...lineItems, emptyLineItem()]);
  };

  const handleAddInventoryLineItem = () => {
    setLineItems([...lineItems, emptyInventoryLineItem()]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems(lineItems.map((li, i) => (i === index ? { ...li, [field]: value } : li)));
  };

  const handleProductChange = (index, productId) => {
    const product = inventoryItemsByProductId.get(productId);
    setLineItems(lineItems.map((lineItem, i) => (
      i === index
        ? {
            productId,
            quantity: lineItem.quantity,
            // A different product has a different catalog price, so any discount negotiated
            // for the previous product no longer applies — start fresh at list price.
            discount: 0,
            discountPerUnit: 0,
            productName: product?.productName || '',
            description: product?.description || '',
            unit: product?.unit || '',
            unitPrice: product?.unitPrice
          }
        : lineItem
    )));
  };

  const inventoryItemFor = (lineItem) => {
    if (!isProductBacked(lineItem) || !lineItem.productId) return null;
    return inventoryItemsByProductId.get(lineItem.productId) || {
      productId: lineItem.productId,
      productName: lineItem.productName,
      description: lineItem.description,
      unit: lineItem.unit,
      unitPrice: lineItem.unitPrice
    };
  };

  // Inventory line items always bill at the catalog unit price server-side — the API's
  // `discount` field is a flat total for the whole line (subtotal = quantity * unitPrice -
  // discount), shared with order-based invoices, and that meaning does not change here.
  // What changes is the admin-facing UI: the Discount field reads and is typed in as a
  // PER-UNIT amount (clearer for "I want to take $X off each one"), and is converted to/from
  // that flat total automatically. "Price to charge" is a scratchpad: type the price you want
  // to charge, it computes the equivalent per-unit discount, then reverts to showing the
  // catalog price again — the catalog price never actually changes.

  const handleQuantityChange = (index, rawValue) => {
    setLineItems(lineItems.map((lineItem, i) => {
      if (i !== index) return lineItem;
      const quantity = Number(rawValue);
      const perUnit = Number(lineItem.discountPerUnit || 0);
      const discount = Number.isFinite(quantity) && Number.isFinite(perUnit)
        ? roundMoney(perUnit * quantity)
        : lineItem.discount;
      return { ...lineItem, quantity: rawValue, discount };
    }));
  };

  const handleDiscountPerUnitChange = (index, rawValue) => {
    setLineItems(lineItems.map((lineItem, i) => {
      if (i !== index) return lineItem;
      const quantity = Number(lineItem.quantity);
      const perUnit = Number(rawValue);
      const discount = Number.isFinite(quantity) && Number.isFinite(perUnit)
        ? roundMoney(perUnit * quantity)
        : NaN;
      return { ...lineItem, discountPerUnit: rawValue, discount };
    }));
  };

  const handleChargedPriceChange = (index, rawValue) => {
    setLineItems(lineItems.map((lineItem, i) => {
      if (i !== index) return lineItem;
      const catalogUnitPrice = Number(inventoryItemFor(lineItem)?.unitPrice);
      const quantity = Number(lineItem.quantity);
      const chargedPrice = Number(rawValue);
      const perUnit = catalogUnitPrice - chargedPrice;
      const discount = Number.isFinite(quantity) && Number.isFinite(perUnit)
        ? roundMoney(perUnit * quantity)
        : lineItem.discount;
      return {
        ...lineItem,
        chargedPriceInput: rawValue,
        discountPerUnit: Number.isFinite(perUnit) ? roundMoney(perUnit) : lineItem.discountPerUnit,
        discount
      };
    }));
  };

  const handleChargedPriceBlur = (index) => {
    setLineItems(lineItems.map((lineItem, i) => (
      i === index ? { ...lineItem, chargedPriceInput: undefined } : lineItem
    )));
  };

  const lineItemRequest = (lineItem) => {
    if (isProductBacked(lineItem)) {
      return {
        productId: lineItem.productId,
        quantity: Number(lineItem.quantity),
        discount: Number(lineItem.discount) || 0
      };
    }
    return {
      description: lineItem.description,
      quantity: Number(lineItem.quantity),
      unitPrice: Number(lineItem.unitPrice),
      discount: Number(lineItem.discount) || 0
    };
  };

  const lineItemsBlocked = () => {
    if (lineItems.length === 0) return true;
    const requestedByProduct = {};
    for (const lineItem of lineItems) {
      const quantity = Number(lineItem.quantity);
      const discount = Number(lineItem.discount || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) return true;
      if (!Number.isFinite(discount) || discount < 0) return true;

      if (isProductBacked(lineItem)) {
        if (typeof lineItem.productId !== 'string' || !lineItem.productId) return true;
        if (conflictingInventoryProductIdSet.has(lineItem.productId)) return true;
        const product = inventoryItemFor(lineItem);
        const unitPrice = Number(product?.unitPrice);
        if (!Number.isFinite(unitPrice) || unitPrice < 0 || discount > quantity * unitPrice) return true;
        requestedByProduct[lineItem.productId] = (requestedByProduct[lineItem.productId] || 0) + quantity;
      } else {
        const unitPrice = Number(lineItem.unitPrice);
        if (!lineItem.description.trim() || !Number.isFinite(unitPrice) || unitPrice < 0) return true;
        if (discount > quantity * unitPrice) return true;
      }
    }

    for (const [productId, quantity] of Object.entries(requestedByProduct)) {
      const product = inventoryItemsByProductId.get(productId);
      if (product && (
        !Number.isFinite(Number(product.availableQuantity))
        || Number(product.availableQuantity) < 0
        || quantity > Number(product.availableQuantity)
      )) return true;
    }
    return false;
  };

  const isCustomBlocked = () => {
    if (!recipientName.trim()) return true;
    return lineItemsBlocked();
  };

  const isCustomCreateBlocked = () => {
    const recipientMissing = recipientSource === 'user' ? !recipientUserId
      : recipientSource === 'contact' ? !contactId
      : recipientSource === 'new' ? !recipientName.trim()
      : true;
    return recipientMissing || lineItemsBlocked();
  };

  const isOrderBasedBlocked = () => {
    return !recipientUserId || !periodStart || !periodEnd;
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    const companyId = localStorage.getItem('companyId');
    const billToOverride = recipientUserId && billToEdited && billTo ? { billTo } : {};

    try {
      if (isEdit) {
        let body;
        if (editInvoice.invoiceType === 'custom') {
          body = {
            recipient: {
              name: recipientName,
              email: recipientEmail,
              phone: recipientPhone,
              address: recipientAddress
            },
            lineItems: lineItems.map(lineItemRequest),
            taxRate: Number(taxRate) || 0,
            discountTotal: Number(discountTotal) || 0,
            notes,
            dueDate: dueDate || null
          };
        } else {
          body = {
            taxRate: Number(taxRate) || 0,
            discountTotal: Number(discountTotal) || 0,
            notes,
            includeItemBreakdown,
            dueDate: dueDate || null
          };
        }
        if (isRevise) {
          await invoiceClient.reviseInvoice(editInvoice._id, body);
          onSaved(t('Invoice revised successfully.', { defaultValue: 'Invoice revised successfully.' }));
        } else {
          await invoiceClient.updateInvoice(editInvoice._id, body);
          onSaved(t('Invoice updated successfully.'));
        }
      } else if (invoiceType === 'order_based') {
        const body = {
          companyId,
          invoiceType: 'order_based',
          recipientUserId,
          ...billToOverride,
          periodStart: `${periodStart}T00:00:00`,
          periodEnd: `${periodEnd}T23:59:59`,
          taxRate: Number(taxRate) || 0,
          discountTotal: Number(discountTotal) || 0,
          notes,
          includeItemBreakdown,
          ...(preview ? { orderIds: selectedOrderIds } : {}),
          ...(dueDate ? { dueDate } : {})
        };
        await invoiceClient.createInvoice(body);
        onSaved(t('Invoice created successfully.'));
      } else {
        const recipientField = recipientSource === 'user'
          ? { recipientUserId, ...billToOverride }
          : recipientSource === 'contact'
          ? { contactId }
          : {
              recipient: {
                name: recipientName,
                email: recipientEmail,
                phone: recipientPhone,
                address: recipientAddress
              }
            };
        const body = {
          companyId,
          invoiceType: 'custom',
          ...recipientField,
          lineItems: lineItems.map(lineItemRequest),
          taxRate: Number(taxRate) || 0,
          discountTotal: Number(discountTotal) || 0,
          notes,
          ...(dueDate ? { dueDate } : {})
        };
        await invoiceClient.createInvoice(body);
        onSaved(t('Invoice created successfully.'));
      }
    } catch (err) {
      console.error('Error saving invoice', err);
      if (!isEdit && invoiceType === 'order_based') {
        await handlePreview();
        setError(err.response?.data?.error || t('Some selected orders are no longer available. The preview has been refreshed — please review and try again.'));
      } else {
        const message = err.response?.data?.error;
        setError(
          message === 'Invalid product price' || message?.startsWith('Invalid quantity for product ')
            ? t('Inventory data is invalid. Please contact support.')
            : message || t('Something went wrong')
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const previewLineItems = preview?.lineItems || [];
  const selectedLineItems = previewLineItems.filter((li) => selectedOrderIds.includes(li.sourceOrderId));
  const selectedSubtotal = selectedLineItems.reduce((sum, li) => sum + (Number(li.subtotal) || 0), 0);
  const noOrdersSelected = previewLineItems.length > 0 && selectedLineItems.length === 0;
  const toggleOrder = (orderId) => setSelectedOrderIds((prev) => (
    prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
  ));
  const toggleAllOrders = (checked) => setSelectedOrderIds(
    checked ? previewLineItems.map((li) => li.sourceOrderId).filter(Boolean) : []
  );

  const createBlocked = isEdit
    ? (editInvoice?.invoiceType === 'custom' ? isCustomBlocked() : false)
    : (invoiceType === 'order_based' ? (isOrderBasedBlocked() || noOrdersSelected) : isCustomCreateBlocked());

  const showOrderBasedFields = isEdit ? editInvoice.invoiceType === 'order_based' : invoiceType === 'order_based';
  const showCustomFields = isEdit ? editInvoice.invoiceType === 'custom' : invoiceType === 'custom';

  const productPickerLineItem = productPickerIndex !== null ? lineItems[productPickerIndex] : null;
  const productPickerSelectedProduct = productPickerLineItem ? inventoryItemFor(productPickerLineItem) : null;
  const productPickerSearchLower = productPickerSearch.trim().toLowerCase();
  const productPickerCandidates = [
    ...(productPickerSelectedProduct && !inventoryItems.some((item) => item.productId === productPickerSelectedProduct.productId)
      ? [productPickerSelectedProduct]
      : []),
    ...inventoryItems
  ];
  const productPickerOptions = productPickerCandidates
    .filter((item) => !productPickerSearchLower || (item.productName || '').toLowerCase().includes(productPickerSearchLower))
    .map((item) => ({
      id: item.productId,
      name: item.productName,
      description: item.description,
      price: `${item.availableQuantity ?? 0} ${item.unit || ''} ${t('available')}`.replace(/\s+/g, ' ').trim(),
      image: item.productImage,
      disabled: (
        !Number.isFinite(Number(item.unitPrice)) || Number(item.unitPrice) < 0
        || !Number.isFinite(Number(item.availableQuantity)) || Number(item.availableQuantity) <= 0
      ) && item.productId !== productPickerLineItem?.productId
    }));

  const billToUser = users.find((u) => u._id === recipientUserId) || null;
  const billToContact = recipientSource === 'contact'
    ? (invoiceContacts.find((c) => c._id === contactId) || null)
    : null;
  const goToUserBilling = (userId) => navigate(`/users?editUser=${userId}`);
  const handleBillToChange = (next) => {
    setBillTo(next);
    setBillToEdited(true);
  };

  const userPickerSearchLower = userPickerSearch.trim().toLowerCase();
  const userPickerOptions = users
    .filter((u) => (
      !userPickerSearchLower
      || (u.username || '').toLowerCase().includes(userPickerSearchLower)
      || (u.email || '').toLowerCase().includes(userPickerSearchLower)
    ))
    .map((u) => ({ id: u._id, username: u.username, email: u.email, role: u.role }));
  const userPickerValue = userPickerMode === 'custom'
    ? (recipientSource === 'user' ? recipientUserId : '')
    : recipientUserId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isRevise
          ? t('Revise invoice {{number}}', {
              number: editInvoice?.invoiceNumber || '',
              defaultValue: `Revise invoice ${editInvoice?.invoiceNumber || ''}`
            })
          : isEdit ? t('Edit Invoice') : t('Create Invoice')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isRevise && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('Saving creates revision {{n}}: totals are recomputed, inventory is adjusted for quantity changes, the PDF is regenerated, and the recipient sees this invoice marked as revised.', {
              n: (editInvoice?.revision || 0) + 1,
              defaultValue: `Saving creates revision ${(editInvoice?.revision || 0) + 1}: totals are recomputed, inventory is adjusted for quantity changes, the PDF is regenerated, and the recipient sees this invoice marked as revised.`
            })}
          </Alert>
        )}

        {!isEdit && (
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={invoiceType}
              exclusive
              onChange={(e, val) => val && setInvoiceType(val)}
              size="small"
            >
              <ToggleButton value="order_based">{t('Order based')}</ToggleButton>
              <ToggleButton value="custom">{t('Custom')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {isEdit && showOrderBasedFields && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('Recipient')}: {editInvoice.recipient?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Period')}: {editInvoice.periodStart ? new Date(editInvoice.periodStart).toLocaleDateString() : ''} - {editInvoice.periodEnd ? new Date(editInvoice.periodEnd).toLocaleDateString() : ''}
            </Typography>
          </Box>
        )}

        {showOrderBasedFields && !isEdit && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={t('Recipient')}
                value={users.find((u) => u._id === recipientUserId)?.username || ''}
                onClick={(e) => openUserPicker('order', e.currentTarget)}
                InputProps={{
                  readOnly: true,
                  startAdornment: recipientUserId && (
                    <InputAdornment position="start">
                      <Avatar sx={{ width: 26, height: 26, borderRadius: `${radius.sm}px`, fontSize: 13 }}>
                        {users.find((u) => u._id === recipientUserId)?.username?.trim().charAt(0).toUpperCase() || '?'}
                      </Avatar>
                    </InputAdornment>
                  ),
                  endAdornment: <ArrowDropDownIcon sx={{ color: 'action.active' }} />
                }}
                sx={{ '& .MuiInputBase-input': { cursor: 'pointer' }, '& .MuiInputBase-root': { cursor: 'pointer' } }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label={t('Period start')}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.25 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('Period end')}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                />
                <Box sx={{ pb: 1 }}><HelpMark id="invoices.period" variant="outlined" /></Box>
              </Box>
            </Grid>
            {billToUser && billTo && (
              <Grid item xs={12}>
                <BillToBox
                  t={t}
                  value={billTo}
                  editing={billToEditing}
                  onStartEdit={() => setBillToEditing(true)}
                  onStopEdit={() => setBillToEditing(false)}
                  onChange={handleBillToChange}
                  onOpenUser={() => goToUserBilling(billToUser._id)}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={handlePreview}
                disabled={isOrderBasedBlocked() || previewLoading}
              >
                {previewLoading ? <CircularProgress size={20} /> : t('Preview')}
              </Button>
            </Grid>
            {preview && (
              <Grid item xs={12}>
                {preview.count === 0 || !preview.lineItems || preview.lineItems.length === 0 ? (
                  <Alert severity="info">
                    {t('No accepted orders found for the selected user and period.')}
                  </Alert>
                ) : (
                  <Box>
                    {/* Capped height so a busy month scrolls inside the dialog instead of stretching it. */}
                    <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: `${radius.md}px` }}>
                      <OrderBasedInvoiceLineItemsTable
                        lineItems={preview.lineItems}
                        currency={preview.currency}
                        includeItemBreakdown={includeItemBreakdown}
                        stickyHeader
                        selectable
                        selectedOrderIds={selectedOrderIds}
                        onToggleOrder={toggleOrder}
                        onToggleAll={toggleAllOrders}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('Selected {{selected}} of {{total}} orders', { selected: selectedLineItems.length, total: preview.count, defaultValue: `Selected ${selectedLineItems.length} of ${preview.count} orders` })}
                      {' — '}{t('Subtotal')}: {formatCurrency(selectedSubtotal, preview.currency, i18n.language)}
                    </Typography>
                    <Typography variant="caption" color={noOrdersSelected ? 'error.main' : 'text.secondary'} sx={{ display: 'block' }}>
                      {noOrdersSelected
                        ? t('Select at least one order to create the invoice.', { defaultValue: 'Select at least one order to create the invoice.' })
                        : t('Untick any order you do not want on this invoice.', { defaultValue: 'Untick any order you do not want on this invoice.' })}
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        )}

        {showOrderBasedFields && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={includeItemBreakdown}
                    onChange={(e) => setIncludeItemBreakdown(e.target.checked)}
                  />
                )}
                label={t('Include item breakdown')}
              />
              <HelpMark id="invoices.item-breakdown" variant="outlined" />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('Show each product under its order. When off, the invoice lists one row per order.')}
            </Typography>
          </Box>
        )}

        {showCustomFields && (
          <Box sx={{ mb: 2 }}>
            {isEdit ? (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('Recipient name')}
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    required
                    error={!recipientName.trim()}
                    helperText={!recipientName.trim() ? t('Recipient name is required') : ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('Email')}
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('Phone')}
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('Address')}
                    value={recipientAddress}
                    onChange={(e) => setRecipientAddress(e.target.value)}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('Recipients (company users)', { defaultValue: 'Recipients (company users)' })}
                    value={recipientSource === 'user' ? (users.find((u) => u._id === recipientUserId)?.username || '') : ''}
                    onClick={(e) => openUserPicker('custom', e.currentTarget)}
                    InputProps={{
                      readOnly: true,
                      startAdornment: recipientSource === 'user' && recipientUserId && (
                        <InputAdornment position="start">
                          <Avatar sx={{ width: 26, height: 26, borderRadius: `${radius.sm}px`, fontSize: 13 }}>
                            {users.find((u) => u._id === recipientUserId)?.username?.trim().charAt(0).toUpperCase() || '?'}
                          </Avatar>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {recipientSource === 'user' && recipientUserId && (
                            <IconButton
                              size="small"
                              aria-label={t('Clear')}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRecipientSource('');
                                setRecipientUserId('');
                                setBillTo(null);
                                setBillToEditing(false);
                                setBillToEdited(false);
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          <ArrowDropDownIcon sx={{ color: 'action.active' }} />
                        </>
                      )
                    }}
                    sx={{ '& .MuiInputBase-input': { cursor: 'pointer' }, '& .MuiInputBase-root': { cursor: 'pointer' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={invoiceContacts}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : (option.name || ''))}
                    value={
                      recipientSource === 'contact'
                        ? (invoiceContacts.find((c) => c._id === contactId) || null)
                        : (recipientSource === 'new' ? recipientName : null)
                    }
                    onChange={(e, newValue) => {
                      if (newValue && typeof newValue === 'object') {
                        setRecipientSource('contact');
                        setContactId(newValue._id);
                        setRecipientName(newValue.name || '');
                        setRecipientEmail(newValue.email || '');
                        setRecipientPhone(newValue.phone || '');
                        setRecipientAddress(newValue.address || '');
                        setRecipientUserId('');
                      } else if (typeof newValue === 'string' && newValue.trim()) {
                        setRecipientSource('new');
                        setRecipientName(newValue);
                        setContactId('');
                        setRecipientUserId('');
                      } else {
                        setRecipientSource('');
                        setContactId('');
                        setRecipientName('');
                        setRecipientUserId('');
                      }
                    }}
                    onInputChange={(e, newInputValue, reason) => {
                      if (reason === 'input') {
                        setRecipientSource('new');
                        setRecipientName(newInputValue);
                        setContactId('');
                        setRecipientUserId('');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t('Custom recipient', { defaultValue: 'Custom recipient' })} />
                    )}
                  />
                </Grid>
                {recipientSource === 'new' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('Email')}
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('Phone')}
                        value={recipientPhone}
                        onChange={(e) => setRecipientPhone(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('Address')}
                        value={recipientAddress}
                        onChange={(e) => setRecipientAddress(e.target.value)}
                      />
                    </Grid>
                  </>
                )}
                {recipientSource === 'user' && billToUser && billTo && (
                  <Grid item xs={12}>
                    <BillToBox
                      t={t}
                      value={billTo}
                      editing={billToEditing}
                      onStartEdit={() => setBillToEditing(true)}
                      onStopEdit={() => setBillToEditing(false)}
                      onChange={handleBillToChange}
                      onOpenUser={() => goToUserBilling(billToUser._id)}
                    />
                  </Grid>
                )}
                {billToContact && (
                  <Grid item xs={12}>
                    <BillToBox
                      t={t}
                      value={{ name: billToContact.name || '', addressLine1: billToContact.address || '' }}
                      editing={false}
                    />
                  </Grid>
                )}
                {!recipientSource && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      {t('Select an existing custom recipient, or type a new name to create one.')}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('Line items')}</Typography>
            {inventoryLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">{t('Loading inventory…')}</Typography>
              </Box>
            )}
            {inventoryError && (
              <Alert
                severity="warning"
                sx={{ mb: 1 }}
                action={<Button size="small" onClick={fetchInventoryItems}>{t('Retry')}</Button>}
              >
                {inventoryError}
              </Alert>
            )}
            {lineItems.map((li, idx) => {
              const productBacked = isProductBacked(li);
              const selectedProduct = inventoryItemFor(li);
              const availableQuantity = selectedProduct?.availableQuantity;
              const quantity = Number(li.quantity);
              const aggregateQuantity = productBacked && li.productId
                ? lineItems
                    .filter(item => isProductBacked(item) && item.productId === li.productId)
                    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
                : 0;
              const stockExceeded = Number.isFinite(Number(availableQuantity))
                && aggregateQuantity > Number(availableQuantity);
              const grossAmount = quantity * Number(selectedProduct?.unitPrice ?? li.unitPrice);
              const discountInvalid = Number(li.discount || 0) > grossAmount;
              const priceAboveCatalog = productBacked && Number(li.discount || 0) < 0;
              // While the admin is typing, show their keystrokes; once they leave the field,
              // it reverts to showing the (unchanged) catalog price — see handleChargedPriceBlur.
              const chargedPriceValue = li.chargedPriceInput ?? selectedProduct?.unitPrice ?? '';
              const chargedPriceInvalid = productBacked && !!selectedProduct && (
                !Number.isFinite(Number(chargedPriceValue)) || Number(chargedPriceValue) < 0 || priceAboveCatalog
              );
              const discountPerUnitValue = li.discountPerUnit
                ?? (productBacked && quantity > 0 ? roundMoney(Number(li.discount || 0) / quantity) : li.discount);

              return (
                <Grid container spacing={1} key={idx} alignItems="flex-start" sx={{ mb: 1 }}>
                  {productBacked ? (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('Inventory product')}
                          error={!li.productId}
                          value={selectedProduct?.productName || ''}
                          onClick={(e) => openProductPicker(idx, e.currentTarget)}
                          InputProps={{
                            readOnly: true,
                            startAdornment: selectedProduct && (
                              <InputAdornment position="start">
                                <RowThumb src={selectedProduct.productImage} name={selectedProduct.productName} sx={{ width: 30, height: 30 }} />
                              </InputAdornment>
                            ),
                            endAdornment: <ArrowDropDownIcon sx={{ color: 'action.active' }} />
                          }}
                          sx={{ '& .MuiInputBase-input': { cursor: 'pointer' }, '& .MuiInputBase-root': { cursor: 'pointer' } }}
                        />
                        {selectedProduct?.description && (
                          <Typography variant="caption" color="text.secondary">
                            {selectedProduct.description}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('Quantity')}
                          type="number"
                          inputProps={{ min: 0.01, max: availableQuantity, step: 'any' }}
                          value={li.quantity}
                          disabled={Number(availableQuantity) === 0}
                          error={!Number.isFinite(quantity) || quantity <= 0 || stockExceeded}
                          helperText={availableQuantity !== undefined
                            ? `${t('Available')}: ${availableQuantity}${stockExceeded ? ` — ${t('reduce quantity')}` : ''}`
                            : ''}
                          onChange={(e) => handleQuantityChange(idx, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('Price to charge')}
                          type="number"
                          inputProps={{ min: 0, step: 'any' }}
                          value={chargedPriceValue}
                          disabled={!selectedProduct}
                          error={chargedPriceInvalid}
                          helperText={
                            !selectedProduct
                              ? ''
                              : priceAboveCatalog
                                ? t('Cannot charge more than the catalog price ({{price}})', { price: selectedProduct.unitPrice })
                                : t('Catalog price is {{price}}. A lower amount here is applied as a per-unit discount.', { price: selectedProduct.unitPrice })
                          }
                          onChange={(e) => handleChargedPriceChange(idx, e.target.value)}
                          onBlur={() => handleChargedPriceBlur(idx)}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('Description')}
                          value={li.description}
                          onChange={(e) => handleLineItemChange(idx, 'description', e.target.value)}
                          required
                          error={!li.description.trim()}
                        />
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('Quantity')}
                          type="number"
                          inputProps={{ min: 0.01, step: 'any' }}
                          value={li.quantity}
                          error={!Number.isFinite(quantity) || quantity <= 0}
                          onChange={(e) => handleLineItemChange(idx, 'quantity', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('Unit price')}
                          type="number"
                          inputProps={{ min: 0, step: 'any' }}
                          value={li.unitPrice}
                          onChange={(e) => handleLineItemChange(idx, 'unitPrice', e.target.value)}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={4} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('Discount')}
                      type="number"
                      inputProps={{ min: 0, step: 'any' }}
                      value={productBacked ? discountPerUnitValue : li.discount}
                      error={
                        productBacked
                          ? !Number.isFinite(Number(discountPerUnitValue)) || priceAboveCatalog || discountInvalid
                          : !Number.isFinite(Number(li.discount || 0)) || Number(li.discount || 0) < 0 || discountInvalid
                      }
                      helperText={
                        discountInvalid
                          ? t('Discount cannot exceed subtotal')
                          : priceAboveCatalog
                            ? t('Cannot exceed the catalog price')
                            : productBacked
                              ? t('Amount off each unit')
                              : t('Fixed amount')
                      }
                      onChange={(e) => (
                        productBacked
                          ? handleDiscountPerUnitChange(idx, e.target.value)
                          : handleLineItemChange(idx, 'discount', e.target.value)
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <IconButton
                      onClick={() => handleRemoveLineItem(idx)}
                      disabled={lineItems.length === 1}
                      aria-label={t('Delete')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button startIcon={<AddIcon />} onClick={handleAddLineItem} size="small">
                {t('Add line item')}
              </Button>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddInventoryLineItem}
                size="small"
                disabled={inventoryLoading}
              >
                {t('Add inventory item')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('Inventory availability is advisory until the invoice is issued.')}
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('Tax rate')}
              type="number"
              inputProps={{ min: 0 }}
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('Discount total')}
              type="number"
              inputProps={{ min: 0 }}
              value={discountTotal}
              onChange={(e) => setDiscountTotal(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              select
              size="small"
              label={t('Terms', { defaultValue: 'Terms' })}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={dueTerms}
              onChange={(e) => {
                const value = e.target.value;
                setDueTerms(value);
                if (value) setDueDate(localDatePlusDays(Number(value)));
              }}
              helperText={t('Sets the due date from today', { defaultValue: 'Sets the due date from today' })}
            >
              <MenuItem value="">{t('Custom date', { defaultValue: 'Custom date' })}</MenuItem>
              {DUE_TERMS.map((days) => (
                <MenuItem key={days} value={String(days)}>
                  {t('Net {{days}}', { days, defaultValue: `Net ${days}` })}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('Due date', { defaultValue: 'Due date' })}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                setDueTerms('');
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <InvoiceNoteTemplates
              templates={noteTemplates}
              notes={notes}
              onInsert={handleInsertNoteTemplate}
              invoiceClient={invoiceClient}
              companyId={localStorage.getItem('companyId') || ''}
              onTemplatesChanged={onNoteTemplatesChanged}
            />
            <TextField
              fullWidth
              size="small"
              label={t('Notes')}
              multiline
              minRows={2}
              maxRows={8}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>

        <Popover
          open={Boolean(productPickerAnchorEl)}
          anchorEl={productPickerAnchorEl}
          onClose={closeProductPicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, width: 360 }}>
            <ProductPicker
              products={productPickerOptions}
              value={productPickerLineItem?.productId}
              onChange={(productId) => {
                handleProductChange(productPickerIndex, productId);
                closeProductPicker();
              }}
              searchValue={productPickerSearch}
              onSearchChange={(e) => setProductPickerSearch(e.target.value)}
              searchPlaceholder={t('Search inventory...')}
              emptyLabel={t('No inventory items found')}
            />
          </Box>
        </Popover>

        <Popover
          open={Boolean(userPickerAnchorEl)}
          anchorEl={userPickerAnchorEl}
          onClose={closeUserPicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, width: 360 }}>
            <UserPicker
              users={userPickerOptions}
              value={userPickerValue}
              onChange={handleUserPickerSelect}
              searchValue={userPickerSearch}
              onSearchChange={(e) => setUserPickerSearch(e.target.value)}
              searchPlaceholder={t('Search users...')}
              emptyLabel={t('No users found')}
            />
          </Box>
        </Popover>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createBlocked || submitting}
        >
          {submitting
            ? <CircularProgress size={20} />
            : isRevise
              ? t('Save revision', { defaultValue: 'Save revision' })
              : (isEdit ? t('Save') : t('Create'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceCreateDialog;
